import { useEffect, useRef } from 'react';
import { useAuth } from '@contexts/User.context';
import { useMutation } from '@tanstack/react-query';
import Button from 'react-bootstrap/Button';

import Alert from '@components/alert/Alert';
import { getBillingPortalUrl } from '@utils/api';

// .btn-link hovers and presses in --bs-link-hover-color, which the theme doesn't set,
// so it falls back to Bootstrap's blue. Hover and press like the app's other links instead.
const linkHoverColors = {
	'--bs-btn-hover-color': 'rgb(var(--bs-link-hover-color-rgb))',
	'--bs-btn-active-color': 'rgb(var(--bs-link-hover-color-rgb))',
};

/**
 * "Manage billing" button that opens the Stripe billing portal in the same tab.
 *
 * Renders nothing unless the user has a Stripe customer (`stripe_customer_id`),
 * so local-trial users never get the button (the backend 404s without one).
 * Every portal request creates a Stripe portal session, so the request is only
 * made on click, never on mount.
 *
 * Render it at a stable place in the tree: a portal request is dropped when this
 * component unmounts, so a page that swaps it out on a view change loses the click.
 *
 * @param {{ note?: string, show?: boolean }} props `note` is shown after the button,
 *   separated by " · ". `show={false}` renders nothing but keeps the component mounted,
 *   so a portal request that is already running still opens the portal.
 */
const BillingPortal = ({ note, show = true }) => {
	const { currentUser } = useAuth();
	const hasCustomer = !!currentUser?.stripe_customer_id;
	const buttonRef = useRef(null);

	const {
		mutate: openPortal,
		isPending,
		isSuccess,
		isError,
		reset,
	} = useMutation({
		mutationFn: async () => {
			// No show_default_subscriptions: it only adds per-plan sessions to
			// available_subscriptions, which this button doesn't use. portal_url is the same.
			const data = await getBillingPortalUrl();
			if (!data?.portal_url) throw new Error('No portal_url in the billing portal response');
			return data.portal_url;
		},
	});
	// Navigate from the per-call callback: TanStack Query drops it once this component
	// unmounts, so leaving the page before the URL arrives doesn't send the user to Stripe.
	const onClick = () =>
		openPortal(undefined, { onSuccess: portalUrl => window.location.assign(portalUrl) });
	// Stay busy after success too: the browser is still leaving for the portal
	const opening = isPending || isSuccess;

	// Back from the portal can restore this page from the back/forward cache,
	// still busy. Make the button usable again then.
	useEffect(() => {
		const onPageShow = event => {
			if (event.persisted) reset();
		};
		window.addEventListener('pageshow', onPageShow);
		return () => window.removeEventListener('pageshow', onPageShow);
	}, [reset]);

	// The disabled button lost focus; give it back so a keyboard retry is one key press
	useEffect(() => {
		if (isError) buttonRef.current?.focus();
	}, [isError]);

	if (!hasCustomer || !show) return null;

	return (
		<>
			<p className="mt-4">
				<Button
					ref={buttonRef}
					type="button"
					variant="link"
					className="p-0 align-baseline"
					style={linkHoverColors}
					disabled={opening}
					aria-busy={opening}
					onClick={onClick}
				>
					<strong>{opening ? 'Opening billing portal…' : 'Manage billing'}</strong>
				</Button>
				{note && (
					<>
						{' '}
						<span aria-hidden="true">·</span> {note}
					</>
				)}
			</p>
			{isError && (
				<Alert variant="danger">
					We couldn't open the billing portal. Please try again, or contact{' '}
					<a href="mailto:post@biodrone.no">post@biodrone.no</a> if it keeps happening.
				</Alert>
			)}
		</>
	);
};

export default BillingPortal;
